import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import general from '../../utils/general'
import Editor from '../../components/editor'
import useI18n from '../../i18n/useI18n'
import './edit-detail.scss'



const EditUniversityDetail = () => {
  const { t } = useI18n()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()

  const STEPS = useMemo(() => [
    {
      id: 1,
      title: t('UNIV_EDIT_STEP_1_TITLE'),
      subtitle: t('UNIV_EDIT_STEP_1_SUBTITLE'),
      icon: 'zmdi-image',
      fields: ['bannerImage', 'summary']
    },
    {
      id: 2,
      title: t('UNIV_EDIT_STEP_2_TITLE'),
      subtitle: t('UNIV_EDIT_STEP_2_SUBTITLE'),
      icon: 'zmdi-play-circle',
      fields: ['about', 'videoLink']
    },
    {
      id: 3,
      title: t('UNIV_EDIT_STEP_3_TITLE'),
      subtitle: t('UNIV_EDIT_STEP_3_SUBTITLE'),
      icon: 'zmdi-city',
      fields: ['campusInfo', 'rankingInfo']
    },
    {
      id: 4,
      title: t('UNIV_EDIT_STEP_4_TITLE'),
      subtitle: t('UNIV_EDIT_STEP_4_SUBTITLE'),
      icon: 'zmdi-graduation-cap',
      fields: ['applicationInfo', 'scholarshipInfo']
    }
  ], [t])
  
  const [loading, setLoading] = useState(true)
  const [university, setUniversity] = useState(null)
  const [currentStep, setCurrentStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    bannerImage: '',
    summary: '',
    about: '',
    videoLink: '',
    applicationInfo: '',
    campusInfo: '',
    rankingInfo: '',
    scholarshipInfo: ''
  })

  useEffect(() => {
    if (id) {
      fetchUniversityData()
      fetchExistingDetail()
    }
  }, [id])

  const fetchUniversityData = () => {
    setLoading(true)
    dispatch(
      ApiCallActions.Get({
        url: `universities/${id}`,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: ({ data }) => {
          const universityData = data?.data || data
          setUniversity(universityData)
          setLoading(false)
        },
        onError: ({ errorMessage }) => {
          general.notificationError(errorMessage || t('UNIV_EDIT_NOT_FOUND'))
          navigate('/universities')
          setLoading(false)
        }
      })
    )
  }

  const fetchExistingDetail = () => {
    dispatch(
      ApiCallActions.Get({
        url: `universities/${id}/detail`,
        showAlertOnError: false,
        showLoading: false,
        onSuccess: ({ data }) => {
          const detailData = data?.data || data
          if (detailData) {
            setFormData({
              bannerImage: detailData.bannerImage || '',
              summary: detailData.summary || '',
              about: detailData.about || '',
              videoLink: detailData.videoLink || '',
              applicationInfo: detailData.applicationInfo || '',
              campusInfo: detailData.campusInfo || '',
              rankingInfo: detailData.rankingInfo || '',
              scholarshipInfo: detailData.scholarshipInfo || ''
            })
          }
        },
        onError: () => {
          // Detail henüz yok, boş form kullan
        }
      })
    )
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const goToStep = (stepId) => {
    setCurrentStep(stepId)
  }

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1)
    }
  }

  const handleSave = () => {
    setSaving(true)
    
    // Boş olmayan alanları filtrele
    const payload = {}
    Object.keys(formData).forEach(key => {
      if (formData[key] && formData[key].trim() !== '') {
        payload[key] = formData[key].trim()
      }
    })

    dispatch(
      ApiCallActions.Post({
        url: `universities/${id}/detail`,
        data: payload,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: () => {
          general.messageSuccess(t('UNIV_EDIT_SUCCESS_MSG'))
          setSaving(false)
          navigate(`/universities/${id}`)
        },
        onError: ({ errorMessage }) => {
          general.messageError(errorMessage || t('UNIV_EDIT_ERROR_MSG'))
          setSaving(false)
        }
      })
    )
  }

  const getStepStatus = (stepId) => {
    const step = STEPS.find(s => s.id === stepId)
    if (!step) return 'empty'
    
    const hasData = step.fields.some(field => formData[field] && formData[field].trim() !== '')
    const allFilled = step.fields.every(field => formData[field] && formData[field].trim() !== '')
    
    if (allFilled) return 'completed'
    if (hasData) return 'partial'
    return 'empty'
  }

  const renderAllSteps = () => {
    return (
      <>
        {/* Step 1: Genel Bilgiler */}
        <div className={`step-content ${currentStep === 1 ? 'active' : 'hidden'}`}>
          <div className="form-group">
            <label className="form-label">
              <i className="zmdi zmdi-image"></i>
              {t('UNIV_EDIT_BANNER_LABEL')}
            </label>
            <input
              type="url"
              className="form-control"
              placeholder="https://example.com/images/banner.jpg"
              value={formData.bannerImage}
              onChange={(e) => handleInputChange('bannerImage', e.target.value)}
            />
            <small className="form-hint">{t('UNIV_EDIT_BANNER_HINT')}</small>
            {formData.bannerImage && (
              <div className="image-preview">
                <img 
                  src={formData.bannerImage} 
                  alt="Banner Preview" 
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label className="form-label">
              <i className="zmdi zmdi-format-subject"></i>
              {t('UNIV_EDIT_SUMMARY_LABEL')}
            </label>
            <Editor
              value={formData.summary}
              onChange={(value) => handleInputChange('summary', value)}
              placeholder={t('UNIV_EDIT_SUMMARY_PLACEHOLDER')}
              minHeight={150}
            />
            <small className="form-hint">{t('UNIV_EDIT_SUMMARY_HINT')}</small>
          </div>
        </div>

        {/* Step 2: Hakkında & Video */}
        <div className={`step-content ${currentStep === 2 ? 'active' : 'hidden'}`}>
          <div className="form-group">
            <label className="form-label">
              <i className="zmdi zmdi-info"></i>
              {t('UNIV_EDIT_ABOUT_LABEL')}
            </label>
            <Editor
              value={formData.about}
              onChange={(value) => handleInputChange('about', value)}
              placeholder={t('UNIV_EDIT_ABOUT_PLACEHOLDER')}
              minHeight={250}
            />
            <small className="form-hint">{t('UNIV_EDIT_ABOUT_HINT')}</small>
          </div>
          
          <div className="form-group">
            <label className="form-label">
              <i className="zmdi zmdi-youtube-play"></i>
              {t('UNIV_EDIT_VIDEO_LABEL')}
            </label>
            <input
              type="url"
              className="form-control"
              placeholder="https://www.youtube.com/watch?v=example"
              value={formData.videoLink}
              onChange={(e) => handleInputChange('videoLink', e.target.value)}
            />
            <small className="form-hint">{t('UNIV_EDIT_VIDEO_HINT')}</small>
            {formData.videoLink && formData.videoLink.includes('youtube') && (
              <div className="video-preview">
                <i className="zmdi zmdi-play-circle"></i>
                <span>{t('UNIV_EDIT_VIDEO_ADDED')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Step 3: Kampüs & Sıralama */}
        <div className={`step-content ${currentStep === 3 ? 'active' : 'hidden'}`}>
          <div className="form-group">
            <label className="form-label">
              <i className="zmdi zmdi-city"></i>
              {t('UNIV_EDIT_CAMPUS_LABEL')}
            </label>
            <Editor
              value={formData.campusInfo}
              onChange={(value) => handleInputChange('campusInfo', value)}
              placeholder={t('UNIV_EDIT_CAMPUS_PLACEHOLDER')}
              minHeight={200}
            />
            <small className="form-hint">{t('UNIV_EDIT_CAMPUS_HINT')}</small>
          </div>
          
          <div className="form-group">
            <label className="form-label">
              <i className="zmdi zmdi-star"></i>
              {t('UNIV_EDIT_RANKING_LABEL')}
            </label>
            <Editor
              value={formData.rankingInfo}
              onChange={(value) => handleInputChange('rankingInfo', value)}
              placeholder={t('UNIV_EDIT_RANKING_PLACEHOLDER')}
              minHeight={200}
            />
            <small className="form-hint">{t('UNIV_EDIT_RANKING_HINT')}</small>
          </div>
        </div>

        {/* Step 4: Başvuru & Burs */}
        <div className={`step-content ${currentStep === 4 ? 'active' : 'hidden'}`}>
          <div className="form-group">
            <label className="form-label">
              <i className="zmdi zmdi-file-text"></i>
              {t('UNIV_EDIT_APP_INFO_LABEL')}
            </label>
            <Editor
              value={formData.applicationInfo}
              onChange={(value) => handleInputChange('applicationInfo', value)}
              placeholder={t('UNIV_EDIT_APP_INFO_PLACEHOLDER')}
              minHeight={200}
            />
            <small className="form-hint">{t('UNIV_EDIT_APP_INFO_HINT')}</small>
          </div>
          
          <div className="form-group">
            <label className="form-label">
              <i className="zmdi zmdi-money"></i>
              {t('UNIV_EDIT_SCHOLARSHIP_LABEL')}
            </label>
            <Editor
              value={formData.scholarshipInfo}
              onChange={(value) => handleInputChange('scholarshipInfo', value)}
              placeholder={t('UNIV_EDIT_SCHOLARSHIP_PLACEHOLDER')}
              minHeight={200}
            />
            <small className="form-hint">{t('UNIV_EDIT_SCHOLARSHIP_HINT')}</small>
          </div>
        </div>
      </>
    )
  }

  if (loading) {
    return (
      <section className="content">
        <div className="container-fluid">
          <div className="row clearfix">
            <div className="col-12 text-center p-5">
              <i className="zmdi zmdi-refresh zmdi-hc-spin" style={{ fontSize: '48px', color: '#667eea' }}></i>
              <p style={{ marginTop: '20px', fontSize: '18px', color: '#666' }}>{t('COMMON_LOADING')}</p>
            </div>
          </div>
        </div>
      </section>
    )
  }

  const currentStepData = STEPS.find(s => s.id === currentStep)
  const progress = ((currentStep - 1) / (STEPS.length - 1)) * 100

  return (
    <section className="content edit-university-detail-page">
      {/* Breadcrumb Header */}
      <div className="block-header">
        <div className="row">
          <div className="col-lg-7 col-md-6 col-sm-12 text-left">
            <h2>
              {t('UNIV_EDIT_TITLE')}
              <small>{university?.name || t('PROGRAMS_TABLE_COLUMNS_UNIVERSITY')}</small>
            </h2>
          </div>
          <div className="col-lg-5 col-md-6 col-sm-12">
            <button 
              className="btn btn-white btn-icon btn-round float-right m-l-10" 
              type="button"
              onClick={() => navigate(`/universities/${id}`)}
              title={t('COMMON_BACK')}
            >
              <i className="zmdi zmdi-arrow-left"></i>
            </button>
            <ul className="breadcrumb float-md-right">
              <li className="breadcrumb-item">
                <Link to="/dashboard">
                  <i className="zmdi zmdi-home" style={{marginRight: '5px'}}></i> {t('COMMON_BREADCRUMB_HOME')}
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/universities">{t('COMMON_UNIVERSITIES')}</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to={`/universities/${id}`}>{university?.name || t('COMMON_DETAIL')}</Link>
              </li>
              <li className="breadcrumb-item active">{t('COMMON_ACTIONS_EDIT')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Main Editor Content */}
      <div className="editor-container">

        {/* Body */}
        <div className="editor-body">
          {/* Steps Sidebar */}
          <div className="steps-sidebar">
            <div className="steps-header">
              <h3>{t('UNIV_EDIT_STEPS_TITLE')}</h3>
              <div className="progress-info">
                <span>{currentStep}/{STEPS.length}</span>
              </div>
            </div>
            
            <div className="steps-list">
              {STEPS.map((step) => {
                const status = getStepStatus(step.id)
                return (
                  <button
                    key={step.id}
                    className={`step-item ${currentStep === step.id ? 'active' : ''} ${status}`}
                    onClick={() => goToStep(step.id)}
                  >
                    <div className="step-icon">
                      {status === 'completed' ? (
                        <i className="zmdi zmdi-check"></i>
                      ) : (
                        <i className={`zmdi ${step.icon}`}></i>
                      )}
                    </div>
                    <div className="step-info">
                      <span className="step-title">{step.title}</span>
                      <span className="step-subtitle">{step.subtitle}</span>
                    </div>
                    {currentStep === step.id && (
                      <div className="step-arrow">
                        <i className="zmdi zmdi-chevron-right"></i>
                      </div>
                    )}
                  </button>
                )
              })}
            </div>

            <div className="steps-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <span className="progress-text">
                {t('UNIV_EDIT_COMPLETED_PERCENT', { percent: Math.round(progress) })}
              </span>
            </div>
          </div>

          {/* Form Content */}
          <div className="form-container">
            <div className="form-header">
              <div className="current-step-icon">
                <i className={`zmdi ${currentStepData?.icon}`}></i>
              </div>
              <div className="current-step-info">
                <h2>{currentStepData?.title}</h2>
                <p>{currentStepData?.subtitle}</p>
              </div>
            </div>

            <div className="form-body">
              {renderAllSteps()}
            </div>

            <div className="form-footer">
              <div className="footer-left">
                <button 
                  className="btn btn-secondary"
                  onClick={handlePrev}
                  disabled={currentStep === 1}
                >
                  <i className="zmdi zmdi-arrow-left"></i>
                  {t('UNIV_EDIT_PREV')}
                </button>
              </div>
              
              <div className="footer-center">
                <div className="step-dots">
                  {STEPS.map(step => (
                    <button
                      key={step.id}
                      className={`dot ${currentStep === step.id ? 'active' : ''} ${getStepStatus(step.id)}`}
                      onClick={() => goToStep(step.id)}
                    />
                  ))}
                </div>
              </div>

              <div className="footer-right">
                <button 
                  className="btn btn-cancel"
                  onClick={() => navigate(`/universities/${id}`)}
                >
                  <i className="zmdi zmdi-close"></i>
                  {t('COMMON_ACTIONS_CANCEL')}
                </button>
                
                {currentStep < STEPS.length ? (
                  <button 
                    className="btn btn-primary"
                    onClick={handleNext}
                  >
                    {t('UNIV_EDIT_NEXT')}
                    <i className="zmdi zmdi-arrow-right"></i>
                  </button>
                ) : (
                  <button 
                    className="btn btn-success"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <i className="zmdi zmdi-refresh zmdi-hc-spin"></i>
                        {t('QUOTA_FORM_SAVING')}
                      </>
                    ) : (
                      <>
                        <i className="zmdi zmdi-check"></i>
                        {t('COMMON_ACTIONS_SAVE')}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default EditUniversityDetail

